import React, { useCallback, useRef, useState } from 'react';
import { API_BASE_URL } from '../../../config/apiConfig';
import { useTheme } from '../../../contexts/ThemeContext';
import { useNotification } from '../../../contexts/NotificationContext';
import { Button } from '../../../components/ui/Button';
import { Switch } from '../../../components/ui/Switch';
import { cn } from '../../../lib/utils';
import { DocumentTextIcon, InfoIcon, UploadCloudIcon, XIcon } from '../../../components/ui/Icons';
import JournalsImportAnalyticsPanel, { type JournalsImportAnalytics } from './JournalsImportAnalyticsPanel';

type ImportError = {
  journalKey: string;
  docNumber: string;
  message: string;
};

type ImportResult = {
  totalRows: number;
  totalJournals: number;
  created: number;
  savedDraft: number;
  skipped: number;
  failed: number;
  posted: number;
  deleted: number;
  errors: ImportError[];
  analytics?: JournalsImportAnalytics;
};

type ProgressState = {
  percent: number;
  processedJournals: number;
  totalJournals: number;
};

const emptyProgress: ProgressState = {
  percent: 0,
  processedJournals: 0,
  totalJournals: 0,
};

const ACCEPT = '.xls,.xlsx,application/vnd.ms-excel,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet';

function formatBytes(n: number): string {
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
  return `${(n / (1024 * 1024)).toFixed(1)} MB`;
}

function isAcceptableFile(file: File): boolean {
  const n = file.name.toLowerCase();
  if (n.endsWith('.xls') || n.endsWith('.xlsx')) return true;
  const m = (file.type || '').toLowerCase();
  return m.includes('spreadsheet') || m.includes('excel');
}

async function runJournalsImportStream(
  file: File,
  options: { skipExisting: boolean; deleteExisting: boolean; postOnImport: boolean; validateOnly: boolean },
  onProgress: (p: ProgressState) => void,
): Promise<ImportResult> {
  const token = localStorage.getItem('access_token');
  const body = new FormData();
  body.append('file', file);
  body.append('skipExisting', options.skipExisting ? 'true' : 'false');
  body.append('deleteExisting', options.deleteExisting ? 'true' : 'false');
  body.append('postOnImport', options.postOnImport ? 'true' : 'false');
  body.append('validateOnly', options.validateOnly ? 'true' : 'false');

  const res = await fetch(`${API_BASE_URL}/acc-documents/import`, {
    method: 'POST',
    headers: token ? { Authorization: `Bearer ${token}` } : {},
    body,
  });

  if (!res.ok) {
    const err = (await res.json().catch(() => null)) as { message?: string } | null;
    throw new Error(err?.message || `Import failed (${res.status})`);
  }

  const reader = res.body?.getReader();
  if (!reader) throw new Error('No response body from server.');

  const decoder = new TextDecoder();
  let buffer = '';
  let lastResult: ImportResult | null = null;

  const handleLine = (trimmed: string) => {
    if (!trimmed) return;
    const msg = JSON.parse(trimmed) as Record<string, unknown>;
    if (msg.type === 'progress') {
      onProgress({
        percent: Number(msg.percent) || 0,
        processedJournals: Number(msg.processedJournals) || 0,
        totalJournals: Number(msg.totalJournals) || 0,
      });
    } else if (msg.type === 'done') {
      lastResult = {
        totalRows: Number(msg.totalRows) || 0,
        totalJournals: Number(msg.totalJournals) || 0,
        created: Number(msg.created) || 0,
        savedDraft: Number(msg.savedDraft) || Number(msg.savedTemp) || 0,
        skipped: Number(msg.skipped) || 0,
        failed: Number(msg.failed) || 0,
        posted: Number(msg.posted) || 0,
        deleted: Number(msg.deleted) || 0,
        errors: Array.isArray(msg.errors) ? (msg.errors as ImportError[]) : [],
        analytics: msg.analytics as JournalsImportAnalytics | undefined,
      };
    } else if (msg.type === 'error') {
      throw new Error(String(msg.message || 'Import failed'));
    }
  };

  while (true) {
    const { done, value } = await reader.read();
    if (done) break;
    buffer += decoder.decode(value, { stream: true });
    const lines = buffer.split('\n');
    buffer = lines.pop() ?? '';
    for (const line of lines) handleLine(line.trim());
  }
  if (buffer.trim()) handleLine(buffer.trim());

  if (!lastResult) throw new Error('Import finished without a result.');
  return lastResult;
}

async function runJournalsAnalyzeStream(
  file: File,
  postOnImport: boolean,
  onProgress: (p: ProgressState) => void,
): Promise<JournalsImportAnalytics> {
  const token = localStorage.getItem('access_token');
  const body = new FormData();
  body.append('file', file);
  body.append('postOnImport', postOnImport ? 'true' : 'false');

  const res = await fetch(`${API_BASE_URL}/acc-documents/import/analyze`, {
    method: 'POST',
    headers: token ? { Authorization: `Bearer ${token}` } : {},
    body,
  });

  if (!res.ok) {
    const err = (await res.json().catch(() => null)) as { message?: string } | null;
    throw new Error(err?.message || `Analysis failed (${res.status})`);
  }

  const reader = res.body?.getReader();
  if (!reader) throw new Error('No response body from server.');

  const decoder = new TextDecoder();
  let buffer = '';
  let analytics: JournalsImportAnalytics | null = null;

  const handleLine = (trimmed: string) => {
    if (!trimmed) return;
    const msg = JSON.parse(trimmed) as Record<string, unknown>;
    if (msg.type === 'progress') {
      onProgress({
        percent: Number(msg.percent) || 0,
        processedJournals: Number(msg.processedJournals) || 0,
        totalJournals: Number(msg.totalJournals) || 0,
      });
    } else if (msg.type === 'done') {
      analytics = msg.analytics as JournalsImportAnalytics;
    } else if (msg.type === 'error') {
      throw new Error(String(msg.message || 'Analysis failed'));
    }
  };

  while (true) {
    const { done, value } = await reader.read();
    if (done) break;
    buffer += decoder.decode(value, { stream: true });
    const lines = buffer.split('\n');
    buffer = lines.pop() ?? '';
    for (const line of lines) handleLine(line.trim());
  }
  if (buffer.trim()) handleLine(buffer.trim());

  if (!analytics) throw new Error('Analysis finished without a result.');
  return analytics;
}

const JournalsImportPage: React.FC = () => {
  const { colors } = useTheme();
  const { showSuccess, showError } = useNotification();

  const [file, setFile] = useState<File | null>(null);
  const [busy, setBusy] = useState(false);
  const [analyzeBusy, setAnalyzeBusy] = useState(false);
  const [result, setResult] = useState<ImportResult | null>(null);
  const [analytics, setAnalytics] = useState<JournalsImportAnalytics | null>(null);
  const [progress, setProgress] = useState<ProgressState>(emptyProgress);
  const [skipExisting, setSkipExisting] = useState(true);
  const [deleteExisting, setDeleteExisting] = useState(false);
  const [postOnImport, setPostOnImport] = useState(false);
  const [validateOnly, setValidateOnly] = useState(false);

  const [uploadDragActive, setUploadDragActive] = useState(false);
  const uploadDragDepth = useRef(0);
  const uploadFileInputRef = useRef<HTMLInputElement>(null);

  const openFilePicker = useCallback(() => uploadFileInputRef.current?.click(), []);
  const clearFile = useCallback(() => {
    setFile(null);
    setResult(null);
    setAnalytics(null);
    setProgress(emptyProgress);
    if (uploadFileInputRef.current) uploadFileInputRef.current.value = '';
  }, []);

  const onUploadDragEnter = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    uploadDragDepth.current += 1;
    setUploadDragActive(true);
  }, []);

  const onUploadDragLeave = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    uploadDragDepth.current -= 1;
    if (uploadDragDepth.current <= 0) {
      uploadDragDepth.current = 0;
      setUploadDragActive(false);
    }
  }, []);

  const onUploadDragOver = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
  }, []);

  const acceptFile = useCallback((f: File | null | undefined) => {
    if (!f) return;
    if (!isAcceptableFile(f)) {
      showError('Please choose an Excel file (.xls or .xlsx).');
      return;
    }
    setFile(f);
    setResult(null);
    setAnalytics(null);
    setProgress(emptyProgress);
  }, [showError]);

  const onUploadDrop = useCallback(
    (e: React.DragEvent) => {
      e.preventDefault();
      e.stopPropagation();
      uploadDragDepth.current = 0;
      setUploadDragActive(false);
      if (busy) return;
      const dropped = e.dataTransfer.files?.[0];
      acceptFile(dropped);
    },
    [acceptFile, busy],
  );

  const onFileInputChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      acceptFile(e.target.files?.[0]);
    },
    [acceptFile],
  );

  const onAnalyze = useCallback(async () => {
    if (!file) return;

    setAnalyzeBusy(true);
    setAnalytics(null);
    setProgress(emptyProgress);

    try {
      const report = await runJournalsAnalyzeStream(file, postOnImport, setProgress);
      setAnalytics(report);
      const blocked = report.summary.blockedJournals;
      if (blocked === 0) {
        showSuccess(`Analysis complete: ${report.summary.readyToImport} journal(s) ready to import.`);
      } else {
        showError(`Analysis found ${blocked} blocked journal(s). See report below.`);
      }
    } catch (err) {
      showError(err instanceof Error ? err.message : 'Analysis failed');
    } finally {
      setAnalyzeBusy(false);
    }
  }, [file, postOnImport, showSuccess, showError]);

  const onSubmit = useCallback(
    async (e: React.FormEvent) => {
      e.preventDefault();
      if (!file) return;

      setBusy(true);
      setResult(null);
      setProgress(emptyProgress);

      try {
        const importResult = await runJournalsImportStream(
          file,
          { skipExisting, deleteExisting, postOnImport, validateOnly },
          setProgress,
        );
        setResult(importResult);
        if (importResult.analytics) {
          setAnalytics(importResult.analytics);
        }
        if (validateOnly) {
          showSuccess(
            `Validation complete: ${importResult.created} journals OK, ${importResult.failed} with issues.`,
          );
        } else if (importResult.failed > 0 || importResult.savedDraft > 0) {
          const parts = [
            `${importResult.created} saved as draft/posted`,
            importResult.savedDraft ? `${importResult.savedDraft} saved as draft with import issues (fix in Journals)` : '',
            importResult.failed ? `${importResult.failed} could not be saved` : '',
          ].filter(Boolean);
          showSuccess(`Import finished: ${parts.join(', ')}.`);
        } else {
          showSuccess(
            `Imported ${importResult.created} journal(s)${importResult.skipped ? `, skipped ${importResult.skipped} existing` : ''}.`,
          );
        }
      } catch (err) {
        showError(err instanceof Error ? err.message : 'Import failed');
      } finally {
        setBusy(false);
      }
    },
    [file, skipExisting, deleteExisting, postOnImport, validateOnly, showSuccess, showError],
  );

  return (
    <div className="max-w-4xl mx-auto space-y-6 pb-10">
      <header>
        <h1 className={cn('text-2xl font-bold tracking-tight', colors.text)}>Journals import</h1>
        <p className={cn('mt-2 text-sm leading-relaxed', colors.textSecondary)}>
          Import historical journal entries from a legacy Excel export (e.g. <code className="text-xs">2026.xls</code>).
          Import companies and chart of accounts first, and ensure the posting period is open for the dates in the file.
          Use <strong>Run analysis</strong> first to see what will block the import.
        </p>
      </header>

      <div className={cn('rounded-xl border px-4 py-3 flex gap-3 text-sm', colors.border, 'bg-amber-500/5 border-amber-500/30')}>
        <InfoIcon className="w-5 h-5 shrink-0 text-amber-600 dark:text-amber-400 mt-0.5" aria-hidden />
        <div className={colors.textSecondary}>
          <p className={cn('font-medium', colors.text)}>Expected columns</p>
          <p className="mt-1">
            Doc Date, Doc, Cmp, Due Date, L4 Code, Details, Non.Ord, Rate, Curr., DL, DUSD, CL, CUSD, Link No.
            <strong>Curr.</strong> is matched to <code>Currency.code</code> (e.g. EUR, USD, YEN).
            Rows are grouped into one journal per <strong>Doc Date + Doc</strong> (e.g. 01/01/2026 + 3 → one document with all its lines). L4 Code maps to account <code>combLevel</code> (dots removed).
            Lines with zero <strong>DL/CL</strong> but amounts in <strong>DUSD/CUSD</strong> are treated as <strong>exchange difference</strong> entries (local stays 0; USD is stored as-is).
            Journals that fail validation are still imported as <strong>Draft</strong> so you can fix them in Accounting → Journals; only journals with no resolvable lines are left out.
          </p>
        </div>
      </div>

      <form onSubmit={onSubmit}>
        <input ref={uploadFileInputRef} type="file" accept={ACCEPT} className="hidden" onChange={onFileInputChange} />

        <div className={cn('rounded-2xl border shadow-sm overflow-hidden', colors.border, colors.surface)}>
          <div className={cn('px-5 py-4 border-b space-y-4', colors.border, colors.surfaceHover)}>
            <Switch
              checked={deleteExisting}
              disabled={busy || analyzeBusy || validateOnly}
              onChange={(e) => setDeleteExisting(e.target.checked)}
              label="Delete existing journals before import"
              description="Removes all journals (draft, temp, and saved). Use for a full re-import."
              labelPosition="right"
            />
            <Switch
              checked={skipExisting}
              disabled={busy || analyzeBusy || deleteExisting}
              onChange={(e) => setSkipExisting(e.target.checked)}
              label="Skip existing journals with the same Doc number and posting date"
              description={deleteExisting ? 'Not needed when deleting all journals first.' : undefined}
              labelPosition="right"
            />
            <Switch
              checked={postOnImport}
              disabled={busy || analyzeBusy || validateOnly}
              onChange={(e) => setPostOnImport(e.target.checked)}
              label="Post on import — save as posted (SAVED) with legacy Doc as journal number"
              description="Multi-company journals require intercompany accounts on each company."
              labelPosition="right"
            />
            <Switch
              checked={validateOnly}
              disabled={busy || analyzeBusy}
              onChange={(e) => setValidateOnly(e.target.checked)}
              label="Validate only — check lookups and balance without writing to the database"
              labelPosition="right"
            />
          </div>

          <div
            role="button"
            tabIndex={0}
            aria-label="Drop zone for journals Excel file"
            onKeyDown={(e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                if (!busy) openFilePicker();
              }
            }}
            onDragEnter={onUploadDragEnter}
            onDragLeave={onUploadDragLeave}
            onDragOver={onUploadDragOver}
            onDrop={onUploadDrop}
            onClick={(e) => {
              if (busy) return;
              if ((e.target as HTMLElement).closest('[data-no-dropzone-click]')) return;
              openFilePicker();
            }}
            className={cn(
              'relative min-h-[220px] px-6 py-10 cursor-pointer transition-all duration-200',
              busy && 'pointer-events-none opacity-60',
              analyzeBusy && 'pointer-events-none opacity-60',
              uploadDragActive
                ? 'ring-2 ring-inset ring-indigo-500 border-indigo-500/40 dark:ring-indigo-400 scale-[1.002]'
                : cn('border-2 border-dashed border-transparent', 'hover:border-indigo-400/50 dark:hover:border-indigo-500/40'),
            )}
          >
            <div className="relative flex flex-col items-center justify-center text-center gap-3 max-w-lg mx-auto">
              <div className={cn('flex h-16 w-16 items-center justify-center rounded-2xl', uploadDragActive ? 'bg-indigo-500/25 text-indigo-700 dark:text-indigo-200 scale-110' : 'bg-indigo-500/10 text-indigo-600 dark:text-indigo-400')}>
                <UploadCloudIcon className="w-9 h-9" aria-hidden />
              </div>
              <p className={cn('text-lg font-semibold', colors.text)}>{uploadDragActive ? 'Release to select file' : 'Drop journals file here'}</p>
              <p className={cn('text-sm mt-1', colors.textSecondary)}>
                <button
                  type="button"
                  data-no-dropzone-click
                  onClick={(ev) => {
                    ev.stopPropagation();
                    if (!busy) openFilePicker();
                  }}
                  className={cn('font-semibold underline decoration-indigo-500/60 underline-offset-2', colors.primaryFg)}
                >
                  Browse files
                </button>
                <span className="mx-1.5 opacity-50">·</span>
                <span>.xls, .xlsx</span>
              </p>
            </div>
          </div>

          {(busy || analyzeBusy) && progress.totalJournals > 0 && (
            <div className={cn('px-5 py-3 border-t', colors.border)}>
              <div className="flex justify-between text-xs mb-1.5">
                <span className={colors.textMuted}>
                  Journal {progress.processedJournals} / {progress.totalJournals}
                </span>
                <span className={colors.textMuted}>{progress.percent}%</span>
              </div>
              <div className={cn('h-2 rounded-full overflow-hidden', colors.surfaceHover)}>
                <div className="h-full bg-indigo-500 transition-all duration-300" style={{ width: `${progress.percent}%` }} />
              </div>
            </div>
          )}

          {(busy || analyzeBusy) && progress.totalJournals === 0 && progress.percent > 0 && (
            <div className={cn('px-5 py-3 border-t', colors.border)}>
              <div className="flex justify-between text-xs mb-1.5">
                <span className={colors.textMuted}>Analyzing…</span>
                <span className={colors.textMuted}>{progress.percent}%</span>
              </div>
              <div className={cn('h-2 rounded-full overflow-hidden', colors.surfaceHover)}>
                <div className="h-full bg-indigo-500 transition-all duration-300" style={{ width: `${progress.percent}%` }} />
              </div>
            </div>
          )}

          <div className={cn('flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 px-5 py-4 border-t', colors.border, colors.surfaceHover)}>
            <div className="flex items-center gap-3 min-w-0 flex-1">
              <DocumentTextIcon className="w-5 h-5 shrink-0 text-indigo-600/80 dark:text-indigo-400/90" aria-hidden />
              <div className="min-w-0">
                {file ? (
                  <>
                    <p className={cn('text-sm font-medium truncate', colors.text)} title={file.name}>
                      {file.name}
                    </p>
                    <p className={cn('text-xs', colors.textMuted)}>{formatBytes(file.size)}</p>
                  </>
                ) : (
                  <p className={cn('text-sm', colors.textMuted)}>No file selected yet.</p>
                )}
              </div>
            </div>
            <div className="flex items-center gap-2 shrink-0 justify-end flex-wrap">
              {file && (
                <Button type="button" variant="ghost" size="sm" data-no-dropzone-click onClick={clearFile} disabled={busy || analyzeBusy} className="inline-flex items-center gap-1.5">
                  <XIcon className="w-4 h-4" aria-hidden />
                  Clear
                </Button>
              )}
              <Button
                type="button"
                variant="secondary"
                data-no-dropzone-click
                loading={analyzeBusy}
                disabled={busy || analyzeBusy || !file}
                onClick={onAnalyze}
                className="inline-flex items-center justify-center gap-2 px-5 rounded-xl"
              >
                <InfoIcon className="w-4 h-4 shrink-0" aria-hidden />
                {analyzeBusy ? 'Analyzing…' : 'Run analysis'}
              </Button>
              <Button type="submit" data-no-dropzone-click loading={busy} disabled={busy || analyzeBusy || !file} className="inline-flex items-center justify-center gap-2 px-6 rounded-xl shadow-sm">
                <UploadCloudIcon className="w-4 h-4 shrink-0" aria-hidden />
                {busy ? (validateOnly ? 'Validating…' : 'Importing…') : validateOnly ? 'Run validation' : 'Run import'}
              </Button>
            </div>
          </div>
        </div>
      </form>

      {analytics && <JournalsImportAnalyticsPanel analytics={analytics} />}

      {result && (
        <section className={cn('rounded-2xl border shadow-sm overflow-hidden', colors.border, colors.surface)}>
          <div className={cn('px-5 py-4 border-b', colors.border, colors.surfaceHover)}>
            <h2 className={cn('text-base font-semibold', colors.text)}>Last import result</h2>
          </div>
          <div className="px-5 py-4">
            <ul className={cn('grid gap-3 sm:grid-cols-2 text-sm', colors.textSecondary)}>
              {[
                ['Lines read', result.totalRows],
                ['Journals', result.totalJournals],
                [validateOnly ? 'Valid' : 'Created', result.created],
                [validateOnly ? '—' : 'Deleted first', result.deleted ?? 0],
                [validateOnly ? '—' : 'Saved as draft (issues)', result.savedDraft ?? 0],
                ['Skipped (existing)', result.skipped],
                ['Failed (not saved)', result.failed],
                ['Posted', result.posted],
              ].map(([label, value]) => (
                <li key={String(label)} className={cn('rounded-xl border px-4 py-3', colors.border, colors.surfaceHover)}>
                  <span className={cn('text-xs font-medium uppercase tracking-wide', colors.textMuted)}>{label}</span>
                  <p className={cn('mt-1 text-lg font-semibold tabular-nums', colors.text)}>{value}</p>
                </li>
              ))}
            </ul>

            {result.errors.length > 0 && (
              <div className="mt-5">
                <h3 className={cn('text-sm font-semibold mb-2', colors.text)}>
                  {validateOnly ? 'Validation issues' : 'Import notes'} (first {result.errors.length})
                </h3>
                <ul className={cn('space-y-2 text-xs max-h-64 overflow-y-auto', colors.textSecondary)}>
                  {result.errors.map((err) => (
                    <li key={`${err.journalKey}-${err.message}`} className={cn('rounded-lg border px-3 py-2', colors.border)}>
                      <span className={cn('font-medium', colors.text)}>Doc {err.docNumber}</span>
                      <span className="mx-1.5 opacity-40">·</span>
                      {err.message}
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </div>
        </section>
      )}
    </div>
  );
};

export default JournalsImportPage;
